import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ENV } from "@/lib/env";
import { supabase } from "@/lib/supabase";

const MURPH_ORANGE = "#FF6B35";
const IPAI_GREEN = "#a78bfa";
const EVENT_DATE = "22nd August 2026";

function isEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export default function MurphPartnership() {
  const [email, setEmail] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const nav = useNavigate();

  // Store partnership tracking on mount
  useState(() => {
    localStorage.setItem("partner", "murph-marathon");
  });

  const sendCode = async () => {
    if (sending) return;
    setErr(null);

    if (!isEmail(email)) return setErr("Please enter a valid email address.");
    if (!agreedTerms) return setErr("Please agree to the terms to continue.");

    try {
      setSending(true);

      const res = await fetch(`${ENV.EDGE_BASE}/auth-email-login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${ENV.SUPABASE_ANON}`,
        },
        body: JSON.stringify({ email }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to start login");

      const mode = json.mode as "signup" | "login";

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: mode === "signup" },
      });

      if (error) {
        if ((error as any).status === 429) {
          throw new Error("Too many attempts. Please wait a minute.");
        }
        throw error;
      }

      localStorage.setItem("signup_email", email);
      localStorage.setItem("otp_type", mode);
      nav("/check-email?partner=murph-marathon");
    } catch (e: any) {
      setErr(e.message || "Failed to send code");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-[#e2e8f0]">
        <div className="flex items-center justify-between px-4 py-3 max-w-screen-xl mx-auto">
          {/* Dual branding */}
          <div className="flex items-center gap-3">
            {/* Input logo */}
            <div className="flex items-center gap-1.5">
              <div className="w-7 h-7 rounded-full bg-[#a78bfa] flex items-center justify-center">
                <svg className="w-4 h-4 text-[#0f172a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="font-bold text-[#0f172a] text-sm hidden sm:inline">Input</span>
            </div>

            {/* Divider */}
            <span className="text-gray-600 text-lg font-light">x</span>

            {/* Murph Marathon logo */}
            <div className="flex items-center gap-1.5">
              <div className="w-7 h-7 rounded-full flex items-center justify-center font-black text-[10px] text-[#0f172a]" style={{ background: MURPH_ORANGE }}>
                MPH
              </div>
              <span className="font-bold text-[#0f172a] text-sm hidden sm:inline">Murph Marathon</span>
            </div>
          </div>

          <a
            href="#signup"
            className="px-5 py-2 rounded-lg font-bold text-sm text-[#0f172a] transition"
            style={{ background: MURPH_ORANGE }}
          >
            Sign Up Free
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Gradient background */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: `radial-gradient(ellipse at 30% 20%, ${MURPH_ORANGE}40, transparent 60%), radial-gradient(ellipse at 70% 80%, ${IPAI_GREEN}20, transparent 60%)`,
          }}
        />

        <div className="relative max-w-3xl mx-auto px-4 pt-16 pb-12 sm:pt-24 sm:pb-16 text-center">
          {/* Partnership badge */}
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-8"
            style={{
              background: `${MURPH_ORANGE}15`,
              border: `1px solid ${MURPH_ORANGE}40`,
              color: MURPH_ORANGE,
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: MURPH_ORANGE }} />
            Official Partnership
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
            Train for{" "}
            <span style={{ color: MURPH_ORANGE }}>Murph Marathon 2</span>
            <br />
            with{" "}
            <span style={{ color: IPAI_GREEN }}>AI Coaching</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-500 max-w-xl mx-auto mb-4 leading-relaxed">
            Input and Murph Marathon have partnered to give all participants{" "}
            <strong className="text-[#0f172a]">free access</strong> to AI-powered training, nutrition, and recovery coaching until the event.
          </p>

          <p className="text-base text-gray-500 mb-10">
            Supporting{" "}
            <span className="text-[#0f172a] font-medium">Transcend Sarcoma</span>{" "}
            &middot; {EVENT_DATE}
          </p>

          {/* INLINE SIGNUP FORM */}
          <div
            id="signup"
            className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-5 sm:p-6 max-w-md mx-auto text-left"
          >
            <h2 className="text-lg font-bold text-[#0f172a] mb-1 text-center">
              Get free access until the event
            </h2>
            <p className="text-sm text-gray-500 mb-5 text-center">
              No credit card required &middot; Free until {EVENT_DATE}
            </p>

            {/* Email Input */}
            <div className="mb-4">
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendCode()}
                className="w-full h-12 px-4 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] text-base placeholder-gray-600 focus:outline-none transition-all"
                style={{ borderColor: email ? `${MURPH_ORANGE}50` : undefined }}
                autoFocus
              />
            </div>

            {/* Terms */}
            <label className="flex items-start gap-3 cursor-pointer group mb-5">
              <div className="relative mt-0.5">
                <input
                  type="checkbox"
                  checked={agreedTerms}
                  onChange={(e) => setAgreedTerms(e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`w-5 h-5 rounded-md border-2 transition-all duration-200 flex items-center justify-center ${
                    agreedTerms
                      ? "border-transparent"
                      : "border-[#e2e8f0] group-hover:border-[#e2e8f0] bg-[#f1f5f9]"
                  }`}
                  style={agreedTerms ? { background: MURPH_ORANGE, borderColor: MURPH_ORANGE } : undefined}
                >
                  {agreedTerms && (
                    <svg className="w-3 h-3 text-[#0f172a]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-gray-500 leading-relaxed">
                I agree to the{" "}
                <Link to="/terms" target="_blank" className="text-[#0f172a] hover:underline">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" target="_blank" className="text-[#0f172a] hover:underline">
                  Privacy Policy
                </Link>
              </span>
            </label>

            {/* CTA Button */}
            <button
              onClick={sendCode}
              disabled={sending || !email || !agreedTerms}
              className="w-full h-12 font-semibold text-base rounded-xl transition-all duration-200 disabled:bg-[#f1f5f9] disabled:text-gray-600 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-[#0f172a]"
              style={
                !sending && email && agreedTerms
                  ? { background: MURPH_ORANGE }
                  : undefined
              }
            >
              {sending ? (
                <>
                  <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Sending code...
                </>
              ) : (
                "Start Free Training"
              )}
            </button>

            {err && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                <p className="text-red-400 text-sm text-center">{err}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-16 sm:py-20 px-4 border-t border-[#e2e8f0]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-4">
            Everything you need to prepare for{" "}
            <span style={{ color: MURPH_ORANGE }}>Murph Marathon 2</span>
          </h2>
          <p className="text-gray-500 text-center mb-12 max-w-lg mx-auto">
            Free AI coaching access for all participants until the event starts.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                icon: (
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
                title: "AI Training Plans",
                desc: "Personalised training programs built for the Murph workout — pull-ups, push-ups, squats, and running.",
                color: IPAI_GREEN,
              },
              {
                icon: (
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
                title: "Nutrition Coaching",
                desc: "AI meal plans and macro tracking to fuel your training and hit the event in peak condition.",
                color: MURPH_ORANGE,
              },
              {
                icon: (
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
                title: "Recovery Insights",
                desc: "Track sleep, HRV, and readiness with Garmin and WHOOP integrations. Train smart, recover smarter.",
                color: "#a855f7",
              },
              {
                icon: (
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
                title: "24/7 AI Coach",
                desc: "Ask anything about your training, nutrition, or recovery — get instant personalised advice.",
                color: "#3b82f6",
              },
              {
                icon: (
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
                title: "Community Leaderboard",
                desc: "Compete with other Murph Marathon participants. Track your progress and see how you stack up.",
                color: "#eab308",
              },
              {
                icon: (
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
                title: "Workout Tracking",
                desc: "Log every session with detailed metrics. Connect Bluetooth devices for real-time tracking.",
                color: IPAI_GREEN,
              },
            ].map((feature, i) => (
              <div
                key={i}
                className="p-5 rounded-2xl border border-[#e2e8f0] bg-[#f8fafc] hover:border-[#e2e8f0] transition-all"
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${feature.color}15`, color: feature.color }}
                >
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-[#0f172a] mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 sm:py-20 px-4 border-t border-[#e2e8f0]" style={{ background: "rgba(248,250,252,0.6)" }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-12">How it works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-12">
            {[
              { step: "1", title: "Sign up", desc: "Enter your email — no credit card needed" },
              { step: "2", title: "Train with AI", desc: "Get a personalised Murph prep plan and daily coaching" },
              { step: "3", title: "Smash the event", desc: "Arrive on August 22nd ready to perform" },
            ].map((s) => (
              <div key={s.step} className="flex flex-col items-center">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold text-[#0f172a] mb-4"
                  style={{
                    background: `linear-gradient(135deg, ${MURPH_ORANGE}, ${IPAI_GREEN})`,
                    boxShadow: `0 8px 30px ${MURPH_ORANGE}40`,
                  }}
                >
                  {s.step}
                </div>
                <h3 className="font-semibold text-[#0f172a] text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEAL BANNER */}
      <section className="py-12 sm:py-16 px-4 border-t border-[#e2e8f0]">
        <div
          className="max-w-2xl mx-auto rounded-2xl p-8 sm:p-10 text-center relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${MURPH_ORANGE}15, ${IPAI_GREEN}10)`,
            border: `1px solid ${MURPH_ORANGE}30`,
          }}
        >
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">
            Free until the event
          </h2>
          <p className="text-gray-500 mb-2 text-lg">
            Full access to Input coaching — completely free until{" "}
            <span className="text-[#0f172a] font-semibold">{EVENT_DATE}</span>
          </p>
          <p className="text-gray-500 text-sm mb-8">
            After the event, continue for just £2.99/week or cancel anytime.
          </p>
          <a
            href="#signup"
            className="inline-flex px-8 py-3.5 rounded-xl font-bold text-base text-[#0f172a] transition"
            style={{ background: MURPH_ORANGE }}
          >
            Sign Up Free Now
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 px-4 border-t border-[#e2e8f0] text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-6 rounded-full bg-[#a78bfa] flex items-center justify-center">
              <svg className="w-3.5 h-3.5 text-[#0f172a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="font-bold text-[#0f172a] text-sm">Input</span>
          </div>
          <span className="text-gray-600">x</span>
          <div className="flex items-center gap-1.5">
            <div className="w-6 h-6 rounded-full flex items-center justify-center font-black text-[8px] text-[#0f172a]" style={{ background: MURPH_ORANGE }}>
              MPH
            </div>
            <span className="font-bold text-[#0f172a] text-sm">Murph Marathon</span>
          </div>
        </div>
        <div className="flex gap-6 justify-center mb-4">
          <Link to="/terms" className="text-gray-500 hover:text-[#0f172a] transition text-sm">Terms</Link>
          <Link to="/privacy" className="text-gray-500 hover:text-[#0f172a] transition text-sm">Privacy</Link>
          <Link to="/contact" className="text-gray-500 hover:text-[#0f172a] transition text-sm">Contact</Link>
        </div>
        <p className="text-gray-600 text-xs">
          © {new Date().getFullYear()} Input. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
